import { Icon, IconProps, Table, TableColumn, Tooltip } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React from 'react';

import { TimestampLink } from './timestamp_link';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { TourVisibilityPrecisionType } from '../../lib/graphql';
import { beautifyString } from '../../lib/util/string';
import { Event, TourEvent } from '../entities';

export interface EventListProps {
  events: Event[];
}

const isTourEvent = (event: Event): event is TourEvent => event.__typename === 'TourEvent';
const eventMetadataParameter = (event: Event, parameterName: string, label: string) => {
  const id = event.metadata?.parameters?.[parameterName];
  return notNil(id) ? (
    <div>
      {label} #{String(id)}
    </div>
  ) : (
    ''
  );
};

export const StreamPrecisionIcon: React.FC<{ precision: TourVisibilityPrecisionType | null }> = ({ precision }) => {
  const iconName: IconProps['name'] =
    precision === TourVisibilityPrecisionType.GPS
      ? 'circle-signal'
      : precision === TourVisibilityPrecisionType.EVENT
        ? 'small-check'
        : 'small-help';

  return (
    <span style={{ display: 'inline-flex' }}>
      <Icon name={iconName} size={16} />
    </span>
  );
};

type EventRow = {
  id: React.Key;
  source: string;
  name: string;
  time: Event['event_time'];
  createdAt: Event['created_at'];
  visibilitySource: Event['visibilitySource'];
  metadata: Event['metadata'];
  originalEvent: Event;
};

const columns: TableColumn<EventRow>[] = [
  {
    id: 'source',
    title: 'Source',
  },
  {
    id: 'name',
    title: 'Name',
  },
  {
    id: 'time',
    title: 'Time',
    cellContent: ({ row }) => <TimestampLink timestamp={row.time} />,
  },
  {
    id: 'createdAt',
    title: 'Created At',
    cellContent: ({ row }) => <FormattedDate date={row.createdAt} />,
  },
  {
    id: 'visibilitySource',
    title: 'Visibility source',
    cellContent: ({ row }) => {
      const vs = row.visibilitySource;
      if (vs === null) {
        return '-';
      }

      const iconName: IconProps['name'] =
        vs.streamPrecision === TourVisibilityPrecisionType.GPS
          ? 'circle-signal'
          : vs.streamPrecision === TourVisibilityPrecisionType.EVENT
            ? 'small-check'
            : 'small-help';

      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Tooltip
            content={
              <>
                <div>Primary event source: {vs.primaryEventSource ?? '-'}</div>
                <div>Stream precision: {vs.streamPrecision ?? '-'}</div>
                <div>Event origin: {vs.eventOrigin ?? '-'}</div>
                <div>
                  Source company:{' '}
                  {vs.sourceCompany !== null
                    ? (vs.sourceCompany.company_name ?? `#${vs.sourceCompany.company_id}`)
                    : '-'}
                </div>
                <div>Source platform: {vs.sourcePlatform?.platform_name ?? '-'}</div>
              </>
            }>
            <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
              <Icon name={iconName} size={16} />
            </span>
          </Tooltip>
        </div>
      );
    },
  },
  {
    id: 'metadata',
    title: 'Metadata',
    cellContent: ({ row }) => {
      const event = row.originalEvent;
      return (
        <div>
          {eventMetadataParameter(event, 'created_at', 'Created at: ')}
          {eventMetadataParameter(event, 'break_id', 'Break: ')}
          {eventMetadataParameter(event, 'external_stop_event_id', 'Ext. stop. ev: ')}
          {eventMetadataParameter(event, 'external_tour_event_id', 'Ext. tour. ev: ')}
          {eventMetadataParameter(event, 'external_delivery_event_id', 'Ext. del. ev: ')}
          {event.metadata !== null ? (
            <div style={{ marginTop: '4px' }}>
              <Tooltip
                content={
                  <>
                    <div>Algorithm: {event.metadata.algorithm ?? '-'}</div>
                    <div>
                      Parameters: {event.metadata.parameters !== null ? JSON.stringify(event.metadata.parameters) : '-'}
                    </div>
                  </>
                }>
                <span style={{ display: 'inline-flex' }}>
                  <Icon name="small-info" />
                </span>
              </Tooltip>
            </div>
          ) : (
            '-'
          )}
        </div>
      );
    },
  },
];

export const EventList: React.FC<EventListProps> = ({ events }) => {
  const getNullSafeTime = (eventTime: string | null) => {
    return eventTime === null ? new Date(3001, 6, 18).getTime() : new Date(eventTime).getTime();
  };
  const sortedEvents = events.sort((event1: Event, event2: Event) => {
    return getNullSafeTime(event1.event_time) - getNullSafeTime(event2.event_time);
  });

  const data: EventRow[] = sortedEvents.map((event, idx) => ({
    id: idx,
    source: isTourEvent(event) ? 'Tour' : `Stop #${event.stop_id}`,
    name:
      beautifyString(event.event_name) +
      (isTourEvent(event) && event.warning?.type !== undefined ? ` (${event.warning.type})` : ''),
    time: event.event_time,
    createdAt: event.created_at,
    visibilitySource: event.visibilitySource,
    metadata: event.metadata,
    originalEvent: event,
  }));

  return (
    <>
      <h3>Event log</h3>
      {data.length === 0 ? <p>No events found</p> : <Table data={data} columns={columns} className="compact-table" />}
    </>
  );
};
